<template>
    <div class="report-goals-editor">
        <div class="mb-2 d-flex org_settings">
            <div class="card pb-4 table-card flex-grow-1">
                <div class="m-3">
                    <h4 class="mt-0">Set your Performance Goals</h4>
                    <div class="d-block d-md-flex align-items-center justify-content-between">
                        <div style="flex: 1; max-width: 300px;">
                            <label class="form-label">Goal Visibility</label>
                            <select v-model="newGoal.visibility_level" class="form-select" :disabled="!canEdit('insights')" aria-label="Goal Visibility">
                                <option value="individual">Individual (Only visible to you)</option>
                                <option value="agency" v-if="user.agency_id !== null">Agency (Visible to your agency)</option>
                                <option value="all">Public (Visible to everyone)</option>
                            </select>
                        </div>
                        
                        <button v-if="canEdit('insights')" class="btn btn-secondary ms-md-3 mt-2 mt-md-0" @click.prevent="showQuickGoalModal">
                            <i class="fas fa-magic"></i> Quick Set Goals
                        </button>
                        <div v-else class="float-end" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                            <button class="btn btn-secondary ms-3" :disabled="true">
                                <i class="fas fa-magic"></i> Quick Set Goals
                            </button>
                        </div>
                    </div>
                </div>


                <div class="card-header">
                    <div class="row">
                        <div class="col-lg-6 col-12">
                            <h3 class="mt-2">{{ fiscalYearRange }}</h3>
                        </div> 
                        <div class="col-lg-6 d-none d-lg-block">
                            <div class="d-flex justify-content-end justify-self-end gap-2">
                                <div class="position-relative">
                                    <button class="btn btn-secondary btn-sm me-2" @click.prevent="previousYear">
                                        <i class="fas fa-chevron-left"></i> Previous Year
                                    </button>
                                    <button class="btn btn-secondary btn-sm me-2" @click.prevent="nextYear">
                                        Next Year <i class="fas fa-chevron-right"></i>
                                    </button>
                                    <button v-if="canEdit('insights')" class="btn btn-primary btn-sm" @click.prevent="clearGoals">
                                        Clear Goals
                                    </button>
                                    <div v-else class="float-end" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                        <button class="btn btn-secondary btn-sm" :disabled="true">
                                            Clear Goals
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> 
                </div>
                <div class="card-body p-0">
                    <editable-goal-table
                        ref="goalTable"
                        :metrics="metrics"
                        :months="months"
                        :goal-data="currentYearGoalData"
                        :read-only="!canEdit('insights')"
                        :invalid-cells="invalidCellList"
                        @cell-change="handleCellChange"
                    />
                </div>
            </div>
        </div>

        <div class="d-flex flex-column">
            <div class="d-flex">
                <button v-if="canEdit('insights')" class="btn btn-primary"  @click.prevent="saveGoal" :disabled="isSaving || hasInvalidValues">
                    <span v-if="isSaving">
                        <i class="fas fa-spinner fa-spin me-1"></i> Saving...
                    </span>
                    <span v-else><img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save</span>
                </button>
                <div v-else class="float-end" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                    <button class="btn btn-secondary ms-2" :disabled="true">
                        <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save
                    </button>
                </div>
                <button 
                    v-if="initialGoal" 
                    class="btn btn-white ms-2" 
                    @click.prevent="$emit('cancel')" 
                    :disabled="isSaving"
                >
                    <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> Cancel
                </button>
            </div>
        </div>

        <!-- Quick Goal Setting Modal -->
        <div class="modal fade" id="quickGoalModal" tabindex="-1" ref="quickGoalModal">
            <div class="modal-dialog modal-lg">
                <div class="modal-content mx-2">
                    <div class="modal-header">
                        <h4 class="modal-title">Calculate Monthly Goals from Annual Targets</h4>
                        <button type="button" class="btn btn-white float-end close-modal" @click.prevent="closeQuickGoalModal" data-bs-toggle="tooltip" title="Close">
                            <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                        </button>
                    </div>
                    <div class="modal-body">
                        <p>Avid can estimate the monthly goals for the selected year 
                            based upon your annual targets and the average distribution from the last three years.</p>
                        <p>Note: Avid is currently only able to calculate goals based on fiscal year.</p>
                        <div class="row">
                            <div class="col-6 mb-3">
                                <label class="form-label">Target Year</label>
                                <select v-model="quickGoal.targetYear" class="form-select" aria-label="Target Year">
                                    <option :value="currentFiscalYear">Fiscal Year {{ currentFiscalYear }}</option>
                                    <option :value="currentFiscalYear + 1">Fiscal Year {{ currentFiscalYear + 1 }}</option>
                                </select>
                            </div>
                            <div class="col-6 mb-3">
                                <label class="form-label">Distribution Method</label>
                                <select v-model="quickGoal.method" class="form-select" aria-label="Distribution Method">
                                    <option value="flat">Historical Average</option>
                                    <option value="linear">Start Easier, Ramp Up Growth</option>
                                </select>
                            </div>
                        </div>
                        
                        <div>
                            <h6>Annual Goals</h6>
                            <div v-for="metric in metrics" :key="metric.key" class="mb-3">
                                <label class="form-label">{{ metric.label }}</label>
                                <div class="input-group">
                                    <input 
                                        type="number" 
                                        class="form-control" 
                                        v-model="quickGoal.goals[metric.key]" 
                                        :placeholder="'Enter annual ' + metric.label.toLowerCase()"
                                     aria-label="{{ metric.label }}">
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button 
                            type="button" 
                            class="btn btn-white" 
                            @click.prevent="closeQuickGoalModal" 
                            :disabled="isApplyingQuickGoals"
                        >
                            Cancel
                        </button>
                        <button 
                            type="button" 
                            class="btn btn-primary" 
                            @click.prevent="applyQuickGoals" 
                            :disabled="!isQuickGoalValid || isApplyingQuickGoals"
                        >
                            <span v-if="isApplyingQuickGoals">
                                <i class="fas fa-spinner fa-spin me-1"></i> Applying...
                            </span>
                            <span v-else>Apply Goals</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';
import { useNotifications } from '../../composables/useNotifications';
import permissionsMixin from '../../mixins/permissions';
import EditableGoalTable from './EditableGoalTable.vue';

export default defineComponent({
    name: 'ReportGoalsEditor',
    components: {
        EditableGoalTable
    },
    mixins: [permissionsMixin],
    setup() {
        const { showNotification } = useNotifications();
        return {
            showNotification
        };
    },
    props: {
        page: {
            type: Object,
            required: true
        },
        user: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        },
        initialGoal: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            isSaving: false,
            isApplyingQuickGoals: false,
            currentYear: new Date().getFullYear(),
            calendarMonths: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
            metrics: [
                { key: 'revenue', label: 'Revenue ($)' },
                { key: 'donors', label: 'Number of New Donors' },
                { key: 'gifts', label: 'Number of Gifts' }
            ],
            newGoal: {
                visibility_level: 'all',
                goal_data: {},
                detailed_goal_data: null
            },
            invalidCells: {},
            quickGoal: {
                targetYear: new Date().getFullYear(),
                method: 'flat',
                goals: {
                    revenue: null,
                    donors: null,
                    gifts: null
                }
            }
        };
    },
    computed: {
        timePeriodType() {
            // Extract time period from page dimensions
            if (!this.page.dimensions) return null;
            
            const timePeriodDimension = this.page.dimensions.find(dim => 
                dim.column && typeof dim.column === 'string' && dim.column.toLowerCase().includes('time_period.year_type')
            );
            
            return timePeriodDimension?.value || null;
        },
        isCalendarYear() {
            // Use Calendar Year ordering only for "Calendar Year" and "Calendar Year to Date"
            // Use Fiscal Year ordering for "Closed Fiscal Year", "Fiscal Year to Date", and "Trailing 12 Months"
            if (Array.isArray(this.timePeriodType)) {
                return this.timePeriodType.some(period => 
                    period === 'Calendar Year' || period === 'Calendar Year to Date'
                );
            }
            return this.timePeriodType === 'Calendar Year' || this.timePeriodType === 'Calendar Year to Date';
        },
        months() {
            // If Calendar Year is selected, use calendar months (no offset)
            // Otherwise use fiscal year months (with offset)
            const offset = this.isCalendarYear ? 0 : (this.client.fiscal_year_offset || 0);
            return [
                ...this.calendarMonths.slice(offset),
                ...this.calendarMonths.slice(0, offset)
            ];
        },
        fiscalYearRange() {
            // Show "Calendar Year" or "FY" based on the time period filter
            if (this.isCalendarYear) {
                return `Calendar Year ${this.currentYear}`;
            }
            return `FY${this.currentYear}`;
        },
        currentFiscalYear() {
            const now = new Date();
            const month = now.getMonth(); // 0-11
            const year = now.getFullYear();
            
            // If Calendar Year is selected, always return current calendar year
            if (this.isCalendarYear) return year;
            
            const offset = this.client.fiscal_year_offset || 0;
            
            // If offset is 0, fiscal year matches calendar year
            if (offset === 0) return year;
            
            // For any month before the fiscal year start, we're in the fiscal year that started in the previous calendar year
            return year + (month < offset ? 0 : 1);
        },
        currentYearGoalData() {
            return this.newGoal.goal_data[this.currentYear] || {};
        },
        hasInvalidValues() {
            return this.invalidCellList.length > 0;
        },
        invalidCellList() {
            return Object.keys(this.invalidCells).map(key => {
                const [metricKey, month] = key.split('|');
                return { metricKey, month };
            });
        },
        isQuickGoalValid() {
            return Object.values(this.quickGoal.goals).some(value => 
                value !== null && value !== '' && Number(value) > 0
            );
        }
    },
    beforeMount() {
        this.initializeGoalData();
    },
    watch: {
        currentYear(newYear) {
            this.ensureYearData(newYear);
            this.resetInvalidCells();
        },
        months() {
            this.ensureYearData(this.currentYear);
        }
    },
    methods: {
        initializeGoalData() {
            if (this.initialGoal) {
                this.newGoal = {
                    visibility_level: this.initialGoal.visibility_level,
                    goal_data: JSON.parse(JSON.stringify(this.initialGoal.goal_data)),
                    detailed_goal_data: this.initialGoal.detailed_goal_data
                };
            }
            this.ensureYearData(this.currentYear);
        },
        ensureYearData(year = this.currentYear) {
            if (!this.newGoal.goal_data[year]) {
                this.initializeYearData(year);
            }
        },
        initializeYearData(year = this.currentYear) {
            // Initialize the goal data structure
            this.newGoal.goal_data[year] = this.newGoal.goal_data[year] || {};
            this.metrics.forEach(metric => {
                this.newGoal.goal_data[year][metric.key] = this.newGoal.goal_data[year][metric.key] || {};
                this.months.forEach(month => {
                    this.newGoal.goal_data[year][metric.key][month] = this.newGoal.goal_data[year][metric.key][month] || 0;
                });
            });
        },
        clearGoals() {
            // Clear goals for the current year
            this.ensureYearData(this.currentYear);

            this.metrics.forEach(metric => {
                this.months.forEach(month => {
                    this.newGoal.goal_data[this.currentYear][metric.key][month] = 0;
                });
            });

            this.resetInvalidCells();
        },
        areAllGoalsZero() {
            if (!this.newGoal.goal_data[this.currentYear]) return true;
            
            return this.metrics.every(metric => {
                const yearData = this.newGoal.goal_data[this.currentYear][metric.key] || {};
                return Object.values(yearData).every(value => Number(value) === 0);
            });
        },
        previousYear() {
            this.currentYear--;
        },
        nextYear() {
            this.currentYear++;
        },
        handleCellChange({ metricKey, month, value }) {
            this.ensureYearData(this.currentYear);

            if (!this.newGoal.goal_data[this.currentYear][metricKey]) {
                this.newGoal.goal_data[this.currentYear][metricKey] = {};
            }

            const parsedValue = this.parseFormattedNumber(value);
            const cellKey = `${metricKey}|${month}`;

            if (value !== '' && value !== null && isNaN(parsedValue)) {
                this.markCellInvalid(cellKey);
                this.newGoal.goal_data[this.currentYear][metricKey][month] = 0;
            } else {
                this.unmarkCellInvalid(cellKey);
                this.newGoal.goal_data[this.currentYear][metricKey][month] = parsedValue || 0;
            }
        },
        markCellInvalid(cellKey) {
            this.invalidCells = {
                ...this.invalidCells,
                [cellKey]: true
            };
        },
        unmarkCellInvalid(cellKey) {
            if (this.invalidCells[cellKey]) {
                const { [cellKey]: removed, ...rest } = this.invalidCells;
                this.invalidCells = rest;
            }
        },
        resetInvalidCells() {
            this.invalidCells = {};
        },
        parseFormattedNumber(value) {
            if (value === null || value === '') return 0;

            // If it's already a number, return it directly
            if (typeof value === 'number') return value;

            const strValue = String(value).trim();
            if (strValue === '') return 0;

            // Remove currency symbols, commas, and other non-numeric characters except decimal point and minus
            const cleanedValue = strValue.replace(/[^0-9.-]/g, '');

            if (cleanedValue === '' && strValue !== '') {
                return NaN;
            }

            // Convert to number
            return Number(cleanedValue);
        },
        formatNumber(value) {
            return new Intl.NumberFormat().format(value);
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value);
        },
        validateTableData() {
            return this.invalidCellList;
        },
        saveGoal() {
            // Validate numeric values first
            const invalidCells = this.validateTableData();
            if (invalidCells.length > 0) {
                //Show the notification
                this.showNotification({
                    text: 'Invalid Entry: All values must be numbers.',
                    type: 'error'
                });
                return;
            }

            this.isSaving = true;

            // If all goals are zero and we have an initial goal, delete it
            if (this.areAllGoalsZero() && this.initialGoal) {
                window.axios.delete('/api/insights/' + this.client.url + '/goals/' + this.initialGoal.id)
                    .then(() => {
                        this.$emit('goal-saved', { goal: null });
                        this.isSaving = false;
                        this.showNotification({
                            text: 'Your goals have been cleared!'
                        });
                    })
                    .catch(error => {
                        //Show the notification
                        this.showNotification({
                            text: 'There was an error clearing your goals. Please try again.',
                            type: 'error'
                        });
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
                return;
            }

            // If all goals are zero and no initial goal, just cancel
            if (this.areAllGoalsZero() && !this.initialGoal) {
                this.$emit('cancel');
                this.isSaving = false;
                return;
            }

            const goalData = {
                report_page_id: this.page.id,
                visibility_level: this.newGoal.visibility_level,
                goal_data: this.newGoal.goal_data,
                detailed_goal_data: null
            };

            if (this.user.agency_id) {
                goalData.agency_id = this.user.agency_id;
            }

            const url = '/api/insights/' + this.client.url + '/goals' + (this.initialGoal ? '/' + this.initialGoal.id : '');
            const method = this.initialGoal ? 'put' : 'post';

            window.axios[method](url, goalData)
                .then(response => {
                    this.$emit('goal-saved', response.data);
                    
                    this.showNotification({
                        text: 'Your goals have been saved!'
                    });
                })
                .catch(error => {
                    //Show the notification
                    this.showNotification({
                        text: 'There was an error saving your goals. Please try again.',
                        type: 'error'
                    });
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },
        showQuickGoalModal() {
            // Reset quick goal data
            this.quickGoal = {
                targetYear: this.currentFiscalYear,
                method: 'flat',
                goals: {
                    revenue: null,
                    donors: null,
                    gifts: null
                }
            };
            
            // Show modal using Bootstrap
            const modal = new bootstrap.Modal(this.$refs.quickGoalModal);
            modal.show();
        },
        
        closeQuickGoalModal() {
            const modal = bootstrap.Modal.getInstance(this.$refs.quickGoalModal);
            if (modal) {
                modal.hide();
            }
        },
        
        async applyQuickGoals() {
            try {
                this.isApplyingQuickGoals = true;

                // Convert any blank/null values to 0
                const processedGoals = {};
                Object.entries(this.quickGoal.goals).forEach(([key, value]) => {
                    processedGoals[key] = (value !== null && value !== '' && Number(value) > 0) ? Number(value) : 0;
                });

                const response = await window.axios.post(
                    `/api/insights/${this.client.url}/goals/estimate`,
                    {
                        page: this.page,
                        goals: processedGoals,
                        method: this.quickGoal.method
                    }
                );

                // Create a mapping for short month names to full month names based on fiscal year
                const monthMapping = {};
                const offset = this.client.fiscal_year_offset || 0;
                const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const fullMonths = this.calendarMonths;
                
                // Reorder months based on fiscal year start
                const reorderedShortMonths = [
                    ...shortMonths.slice(offset),
                    ...shortMonths.slice(0, offset)
                ];
                const reorderedFullMonths = [
                    ...fullMonths.slice(offset),
                    ...fullMonths.slice(0, offset)
                ];
                
                // Create mapping
                reorderedShortMonths.forEach((shortMonth, index) => {
                    monthMapping[shortMonth] = reorderedFullMonths[index];
                });

                // Create a mapping for metric keys
                const metricMapping = {
                    'revenue': 'revenue',
                    'gifts': 'gifts',
                    'active donors': 'donors'
                };

                // Update the goal data for the target year
                if (!this.newGoal.goal_data[this.quickGoal.targetYear]) {
                    this.newGoal.goal_data[this.quickGoal.targetYear] = {};
                }

                // Update each metric's monthly values
                Object.entries(response.data.metrics).forEach(([apiMetric, values]) => {
                    const metricKey = metricMapping[apiMetric];
                    if (!metricKey) return;

                    if (!this.newGoal.goal_data[this.quickGoal.targetYear][metricKey]) {
                        this.newGoal.goal_data[this.quickGoal.targetYear][metricKey] = {};
                    }

                    response.data.months.forEach((shortMonth, index) => {
                        const fullMonth = monthMapping[shortMonth];
                        if (fullMonth) {
                            this.newGoal.goal_data[this.quickGoal.targetYear][metricKey][fullMonth] = values[index];
                        }
                    });
                });

                // Update the current year if needed
                if (this.currentYear !== this.quickGoal.targetYear) {
                    this.currentYear = this.quickGoal.targetYear;
                }

                this.closeQuickGoalModal();

                // Show success message
                this.showNotification({
                    text: 'Your suggested monthly goals have been calculated.'
                });
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'There was an error calculating your suggested monthly goals. Please try again.';
                //Show the notification
                this.showNotification({
                    text: errorMessage,
                    type: 'error'
                });
            } finally {
                this.isApplyingQuickGoals = false;  
            }
        }
    }
});
</script>

<style scoped>
.report-goals-editor {
    position: relative;
}

.modal-dialog {
    max-width: 500px;
}
</style>